"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import {
  circleActionVariantCssVars,
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";

export type GlassTileSize = "sm" | "md" | "lg";
export type GlassTileShape = "rounded" | "circle";

export interface GlassTileProps {
  label: React.ReactNode;
  icon: React.ReactNode;
  variant?: CircleActionVariant;
  selected?: boolean;
  size?: GlassTileSize;
  shape?: GlassTileShape;
  as?: "button" | "div";
  type?: "button" | "submit" | "reset";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  className?: never;
  "aria-label"?: string;
  "aria-pressed"?: boolean;
  /** Explosión de partículas planas al hover (círculos). */
  particleBurst?: boolean;
}

const PARTICLE_COUNT = 24;
const PARTICLE_OUTER_COUNT = 12;

/** Alcance de la explosión (más contenido = menos recorte en el layout). */
const BURST_RADIUS: Record<GlassTileSize, number> = {
  sm: 0.82,
  md: 0.88,
  lg: 0.92,
};

/** Margen invisible para que las partículas no se corten entre tiles. */
const BURST_PAD: Record<GlassTileSize, string> = {
  sm: "p-2.5 -m-2.5",
  md: "p-3 -m-3",
  lg: "p-3.5 -m-3.5",
};

type ParticleSpec = {
  id: string;
  tx: number;
  ty: number;
  dot: number;
  delay: number;
  alt?: boolean;
  ring?: "inner" | "mid" | "outer";
};

function buildParticleSpecs(
  generation: number,
  size: GlassTileSize
): ParticleSpec[] {
  const specs: ParticleSpec[] = [];
  const phase = (generation % 7) * 0.17;
  const reach = BURST_RADIUS[size];

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + phase;
    const dist = (34 + (i % 7) * 7 + (i % 3) * 4) * reach;
    specs.push({
      id: `m-${i}`,
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist,
      dot: 2 + (i % 4),
      delay: i * 7,
      alt: i % 3 === 0,
      ring: "mid",
    });
  }

  for (let i = 0; i < PARTICLE_OUTER_COUNT; i++) {
    const angle = (i / PARTICLE_OUTER_COUNT) * Math.PI * 2 + phase + 0.42;
    const dist = (48 + (i % 5) * 9 + (i % 2) * 5) * reach;
    specs.push({
      id: `o-${i}`,
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist,
      dot: 2 + (i % 3),
      delay: 28 + i * 9,
      alt: i % 2 === 1,
      ring: "outer",
    });
  }

  // Fragmentos cortos — salen primero y dan sensación de estallido
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2 + phase * 1.4;
    const dist = (22 + (i % 4) * 5) * reach;
    specs.push({
      id: `c-${i}`,
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist,
      dot: 3 + (i % 2),
      delay: i * 4,
      alt: i % 2 === 0,
      ring: "inner",
    });
  }

  return specs;
}

function GlassTileParticleBurst({
  generation,
  size,
}: {
  generation: number;
  size: GlassTileSize;
}) {
  if (generation < 1) return null;

  const specs = buildParticleSpecs(generation, size);

  return (
    <span
      className="gt-particle-layer pointer-events-none absolute left-1/2 top-1/2 z-[5] h-0 w-0 overflow-visible"
      aria-hidden
    >
      {specs.map((p) => (
        <span
          key={`${generation}-${p.id}`}
          className={cn(
            "gt-particle",
            p.alt && "gt-particle-alt",
            p.ring === "outer" && "gt-particle-outer",
            p.ring === "inner" && "gt-particle-inner"
          )}
          style={
            {
              "--tx": `${p.tx.toFixed(1)}px`,
              "--ty": `${p.ty.toFixed(1)}px`,
              "--gt-dot-size": `${p.dot}px`,
              animationDelay: `${p.delay}ms`,
            } as React.CSSProperties
          }
        />
      ))}
    </span>
  );
}

const CARD_WIDTH: Record<GlassTileSize, string> = {
  sm: "w-[4.5rem]",
  md: "w-[5.25rem]",
  lg: "w-[5.75rem]",
};

const CIRCLE_SIZE: Record<GlassTileSize, string> = {
  sm: "h-[3.75rem] w-[3.75rem]",
  md: "h-[4.25rem] w-[4.25rem]",
  lg: "h-[5rem] w-[5rem]",
};

const ICON_BOX: Record<GlassTileSize, string> = {
  sm: "h-9 w-9",
  md: "h-10 w-10",
  lg: "h-11 w-11",
};

const LABEL: Record<GlassTileSize, string> = {
  sm: "text-[10px]",
  md: "text-[11px]",
  lg: "text-[11px]",
};

const CIRCLE_LABEL_MAX: Record<GlassTileSize, string> = {
  sm: "max-w-[4.75rem]",
  md: "max-w-[4.75rem]",
  lg: "max-w-[5.5rem]",
};

export const GlassTile = React.forwardRef<
  HTMLButtonElement | HTMLDivElement,
  GlassTileProps
>(function GlassTile(
  {
    label,
    icon,
    variant: variantProp,
    selected = false,
    size = "sm",
    shape = "rounded",
    as = "button",
    type = "button",
    onClick,
    disabled,
    className,
    "aria-label": ariaLabel,
    "aria-pressed": ariaPressed,
    particleBurst,
  },
  ref
) {
  const resolvedVariant = resolveCircleActionVariant(variantProp ?? "slate");
  const variantStyle = circleActionVariantCssVars(resolvedVariant);
  const isCircle = shape === "circle";
  const burstEnabled = particleBurst ?? isCircle;
  const [burstGeneration, setBurstGeneration] = React.useState(0);

  const triggerBurst = React.useCallback(() => {
    if (burstEnabled && !disabled) {
      setBurstGeneration((g) => g + 1);
    }
  }, [burstEnabled, disabled]);

  React.useEffect(() => {
    if (burstGeneration < 1) return;
    const id = burstGeneration;
    const timer = window.setTimeout(() => {
      setBurstGeneration((current) => (current === id ? 0 : current));
    }, 820);
    return () => window.clearTimeout(timer);
  }, [burstGeneration]);

  const focusRingClass =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-2 dark:focus-visible:ring-sky-500/40 dark:focus-visible:ring-offset-zinc-950";

  const checkmark = selected ? (
    <span
      aria-hidden
      className="absolute right-0 top-0 z-[2] flex h-3.5 w-3.5 translate-x-1/4 -translate-y-1/4 items-center justify-center rounded-full bg-[var(--gb-accent-color)] shadow-[0_1px_4px_rgba(15,23,42,0.18)]"
    >
      <svg width="7" height="7" viewBox="0 0 8 8" fill="none">
        <path
          d="M1.5 4l2 2 3-3"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  ) : null;

  const glassSurfaceClass = cn(
    "glass-tile relative",
    "motion-safe:[transition:background_340ms_ease,box-shadow_420ms_cubic-bezier(0.16,1,0.3,1),transform_280ms_cubic-bezier(0.16,1,0.3,1),backdrop-filter_340ms_ease]",
    selected ? "gt-active" : "gt-idle",
    isCircle && "gt-sphere"
  );

  const interactiveWrapClass = cn(
    "flex shrink-0 flex-col items-center",
    isCircle ? "gap-2 overflow-visible bg-transparent p-0 [overflow:visible]" : "gap-1.5",
    as === "button" && "cursor-pointer disabled:cursor-not-allowed disabled:opacity-40",
    focusRingClass,
    className
  );

  const roundedRootClass = cn(
    glassSurfaceClass,
    "flex flex-col items-center justify-center rounded-2xl px-2 py-2.5",
    "select-none font-semibold tracking-[-0.01em]",
    as === "button" && "active:scale-[0.97]",
    CARD_WIDTH[size]
  );

  const content = isCircle ? (
    <>
      <span
        className={cn(
          "gt-sphere-anchor relative inline-flex flex-shrink-0 items-center justify-center overflow-visible",
          burstEnabled && BURST_PAD[size]
        )}
        style={variantStyle as React.CSSProperties}
      >
        {burstEnabled && (
          <GlassTileParticleBurst generation={burstGeneration} size={size} />
        )}
        <span
          className={cn(
            glassSurfaceClass,
            "relative flex flex-shrink-0 items-center justify-center rounded-full",
            CIRCLE_SIZE[size],
            burstEnabled && "gt-sphere-burst-host"
          )}
        >
          <span className="gt-sphere-icon-wrap relative z-[2] flex items-center justify-center">
            {icon}
          </span>
          {checkmark}
        </span>
      </span>
      <span
        className={cn(
          "line-clamp-2 text-center font-semibold leading-tight",
          CIRCLE_LABEL_MAX[size],
          LABEL[size],
          selected ? "text-[var(--gb-accent-color)]" : "text-slate-600 dark:text-zinc-400"
        )}
      >
        {label}
      </span>
    </>
  ) : (
    <>
      <span
        className={cn(
          "relative z-[1] flex flex-shrink-0 items-center justify-center rounded-xl",
          ICON_BOX[size]
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          "relative z-[1] line-clamp-2 text-center leading-tight",
          LABEL[size]
        )}
      >
        {label}
      </span>
      {checkmark}
    </>
  );

  if (as === "div") {
    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={isCircle ? interactiveWrapClass : roundedRootClass}
        style={isCircle ? undefined : (variantStyle as React.CSSProperties)}
        aria-label={ariaLabel}
      >
        {content}
      </div>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type={type}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed ?? selected}
      onClick={onClick}
      onMouseEnter={isCircle && burstEnabled ? triggerBurst : undefined}
      className={isCircle ? interactiveWrapClass : roundedRootClass}
      style={isCircle ? undefined : (variantStyle as React.CSSProperties)}
    >
      {content}
    </button>
  );
});

/** Inyectar una sola vez junto a un grupo de tiles (p. ej. en el contenedor padre). */
export function GlassTileGlobalStyles() {
  return (
    <style jsx global>{`
      @keyframes gt-particle-burst-flat {
        0% {
          transform: translate(-50%, -50%) translate(0, 0) scale(0.2);
          opacity: 1;
          visibility: visible;
        }
        16% {
          transform: translate(-50%, -50%) translate(calc(var(--tx) * 0.52), calc(var(--ty) * 0.52))
            scale(1.12);
          opacity: 1;
        }
        62% {
          transform: translate(-50%, -50%) translate(calc(var(--tx) * 0.94), calc(var(--ty) * 0.94))
            scale(1);
          opacity: 1;
        }
        90% {
          transform: translate(-50%, -50%) translate(var(--tx), var(--ty)) scale(0.88);
          opacity: 1;
        }
        100% {
          transform: translate(-50%, -50%) translate(var(--tx), var(--ty)) scale(0.65);
          opacity: 0;
          visibility: hidden;
        }
      }

      .gt-sphere-anchor {
        overflow: visible;
        clip-path: none;
      }

      .gt-particle-layer {
        overflow: visible;
        clip-path: none;
      }

      .gt-particle {
        position: absolute;
        left: 0;
        top: 0;
        width: var(--gt-dot-size, 3px);
        height: var(--gt-dot-size, 3px);
        border-radius: 9999px;
        background-color: var(--gt-particle-color, var(--gb-accent-color));
        box-shadow: none;
        filter: none;
        opacity: 1;
        visibility: visible;
        animation: gt-particle-burst-flat 0.64s cubic-bezier(0.14, 0.88, 0.22, 1) forwards;
        will-change: transform, opacity;
      }

      .gt-particle-alt {
        background-color: var(--gt-particle-color-alt, var(--gt-particle-color));
      }

      .gt-particle-outer {
        animation-duration: 0.7s;
        animation-timing-function: cubic-bezier(0.1, 0.86, 0.24, 1);
      }

      .gt-particle-inner {
        animation-duration: 0.5s;
        animation-timing-function: cubic-bezier(0.22, 0.95, 0.32, 1);
      }

      .gt-sphere-burst-host {
        overflow: visible;
        isolation: auto;
      }

      button:hover .gt-sphere-anchor,
      button:focus-visible .gt-sphere-anchor {
        z-index: 4;
      }

      /* ── IDLE (mismo lenguaje que glass-badge) ─────────────────────────── */
      .glass-tile.gt-idle {
        isolation: isolate;
        overflow: hidden;
        color: rgb(100 116 139);
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.54) 0%, transparent 58%),
          radial-gradient(circle at 46% 44%, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.36) 38%, rgba(255, 255, 255, 0.08) 58%, transparent 72%),
          radial-gradient(circle at 70% 68%, rgba(255, 255, 255, 0.46) 0%, rgba(255, 255, 255, 0.16) 20%, transparent 34%),
          radial-gradient(circle at 50% 50%, rgba(203, 213, 225, 0.18) 0%, rgba(226, 232, 240, 0.08) 100%),
          rgba(226, 232, 240, 0.55);
        backdrop-filter: blur(22px) saturate(1.35);
        -webkit-backdrop-filter: blur(22px) saturate(1.35);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.72),
          inset -1px 0 0 rgba(255, 255, 255, 0.22),
          inset 0 -1px 0 rgba(255, 255, 255, 0.36),
          inset 0 0 0 1px rgba(255, 255, 255, 0.32),
          inset 1px 1px 0 rgba(15, 23, 42, 0.06),
          0 1px 3px rgba(15, 23, 42, 0.06),
          0 4px 12px -4px rgba(15, 23, 42, 0.08);
      }
      .dark .glass-tile.gt-idle {
        color: rgb(161 161 170);
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.22) 0%, transparent 58%),
          radial-gradient(circle at 46% 44%, rgba(255, 255, 255, 0.32) 0%, rgba(255, 255, 255, 0.1) 40%, transparent 70%),
          radial-gradient(circle at 70% 68%, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.05) 20%, transparent 34%),
          radial-gradient(circle at 50% 50%, rgba(148, 163, 184, 0.12) 0%, rgba(100, 116, 139, 0.05) 100%),
          rgba(148, 163, 184, 0.09);
        backdrop-filter: blur(22px) saturate(1.25) brightness(0.96);
        -webkit-backdrop-filter: blur(22px) saturate(1.25) brightness(0.96);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.24),
          inset -1px 0 0 rgba(255, 255, 255, 0.1),
          inset 0 -1px 0 rgba(255, 255, 255, 0.16),
          inset 0 0 0 1px rgba(255, 255, 255, 0.14),
          inset 1px 1px 0 rgba(0, 0, 0, 0.22),
          0 1px 3px rgba(0, 0, 0, 0.2),
          0 4px 12px -4px rgba(0, 0, 0, 0.28);
      }

      /* Esferas: sin recorte para que la explosión salga fuera del círculo */
      .glass-tile.gt-idle.gt-sphere,
      .glass-tile.gt-active.gt-sphere {
        overflow: visible;
        isolation: auto;
      }

      /* Esferas: glass un poco más ligero que el tile estándar (no invisible) */
      .glass-tile.gt-idle.gt-sphere {
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.44) 0%, transparent 58%),
          radial-gradient(
            circle at 46% 44%,
            rgba(255, 255, 255, 0.68) 0%,
            rgba(255, 255, 255, 0.28) 38%,
            rgba(255, 255, 255, 0.06) 58%,
            transparent 72%
          ),
          radial-gradient(
            circle at 70% 68%,
            rgba(255, 255, 255, 0.36) 0%,
            rgba(255, 255, 255, 0.12) 20%,
            transparent 34%
          ),
          radial-gradient(circle at 50% 50%, rgba(203, 213, 225, 0.14) 0%, rgba(226, 232, 240, 0.06) 100%),
          rgba(226, 232, 240, 0.4);
        backdrop-filter: blur(20px) saturate(1.32);
        -webkit-backdrop-filter: blur(20px) saturate(1.32);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.62),
          inset -1px 0 0 rgba(255, 255, 255, 0.18),
          inset 0 -1px 0 rgba(255, 255, 255, 0.3),
          inset 0 0 0 1px rgba(255, 255, 255, 0.28),
          inset 1px 1px 0 rgba(15, 23, 42, 0.05),
          0 1px 3px rgba(15, 23, 42, 0.05),
          0 4px 12px -4px rgba(15, 23, 42, 0.07);
      }

      .dark .glass-tile.gt-idle.gt-sphere {
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.18) 0%, transparent 58%),
          radial-gradient(
            circle at 46% 44%,
            rgba(255, 255, 255, 0.26) 0%,
            rgba(255, 255, 255, 0.08) 40%,
            transparent 70%
          ),
          radial-gradient(
            circle at 70% 68%,
            rgba(255, 255, 255, 0.13) 0%,
            rgba(255, 255, 255, 0.04) 20%,
            transparent 34%
          ),
          radial-gradient(circle at 50% 50%, rgba(148, 163, 184, 0.1) 0%, rgba(100, 116, 139, 0.04) 100%),
          rgba(148, 163, 184, 0.11);
        backdrop-filter: blur(20px) saturate(1.22) brightness(0.96);
        -webkit-backdrop-filter: blur(20px) saturate(1.22) brightness(0.96);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.2),
          inset -1px 0 0 rgba(255, 255, 255, 0.08),
          inset 0 -1px 0 rgba(255, 255, 255, 0.14),
          inset 0 0 0 1px rgba(255, 255, 255, 0.12),
          inset 1px 1px 0 rgba(0, 0, 0, 0.2),
          0 1px 3px rgba(0, 0, 0, 0.18),
          0 4px 12px -4px rgba(0, 0, 0, 0.24);
      }

      .glass-tile.gt-idle:not(:disabled):hover {
        transform: translateY(-1.5px);
        color: var(--gb-accent-color);
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.62) 0%, transparent 58%),
          radial-gradient(circle at 46% 44%, rgba(255, 255, 255, 0.86) 0%, rgba(255, 255, 255, 0.38) 38%, rgba(255, 255, 255, 0.08) 58%, transparent 72%),
          radial-gradient(circle at 70% 68%, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.18) 20%, transparent 34%),
          var(--gb-tint-bg);
        backdrop-filter: blur(26px) saturate(1.5);
        -webkit-backdrop-filter: blur(26px) saturate(1.5);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.76),
          inset -1px 0 0 rgba(255, 255, 255, 0.24),
          inset 0 -1px 0 rgba(255, 255, 255, 0.4),
          inset 0 0 0 1.5px rgba(255, 255, 255, 0.78),
          inset 1px 1px 0 rgba(15, 23, 42, 0.05),
          0 2px 6px rgba(15, 23, 42, 0.06),
          var(--gb-tint-shadow);
      }
      .dark .glass-tile.gt-idle:not(:disabled):hover {
        color: var(--gb-accent-color-dark);
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.26) 0%, transparent 58%),
          radial-gradient(circle at 46% 44%, rgba(255, 255, 255, 0.38) 0%, rgba(255, 255, 255, 0.12) 40%, transparent 70%),
          radial-gradient(circle at 70% 68%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.06) 20%, transparent 34%),
          var(--gb-tint-bg);
        backdrop-filter: blur(26px) saturate(1.4) brightness(1.02);
        -webkit-backdrop-filter: blur(26px) saturate(1.4) brightness(1.02);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.28),
          inset -1px 0 0 rgba(255, 255, 255, 0.12),
          inset 0 -1px 0 rgba(255, 255, 255, 0.18),
          inset 0 0 0 1.5px rgba(255, 255, 255, 0.32),
          inset 1px 1px 0 rgba(0, 0, 0, 0.18),
          0 2px 6px rgba(0, 0, 0, 0.22),
          var(--gb-tint-shadow);
      }

      /* ── ACTIVE (tinted glass) ─────────────────────────────────────────── */
      .glass-tile.gt-active {
        isolation: isolate;
        overflow: hidden;
        color: var(--gb-accent-color);
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.5) 0%, transparent 58%),
          radial-gradient(circle at 46% 44%, rgba(255, 255, 255, 0.74) 0%, rgba(255, 255, 255, 0.28) 38%, rgba(255, 255, 255, 0.06) 58%, transparent 72%),
          radial-gradient(circle at 70% 68%, rgba(255, 255, 255, 0.38) 0%, rgba(255, 255, 255, 0.12) 20%, transparent 34%),
          var(--gb-tint-bg);
        backdrop-filter: blur(22px) saturate(1.55);
        -webkit-backdrop-filter: blur(22px) saturate(1.55);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.68),
          inset -1px 0 0 rgba(255, 255, 255, 0.2),
          inset 0 -1px 0 rgba(255, 255, 255, 0.32),
          inset 0 0 0 1.5px var(--gb-tint-border),
          inset 1px 1px 0 rgba(15, 23, 42, 0.05),
          0 1px 3px rgba(15, 23, 42, 0.06),
          var(--gb-tint-shadow);
      }
      .dark .glass-tile.gt-active {
        color: var(--gb-accent-color-dark);
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.2) 0%, transparent 58%),
          radial-gradient(circle at 46% 44%, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0.08) 40%, transparent 70%),
          radial-gradient(circle at 70% 68%, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.04) 20%, transparent 34%),
          var(--gb-tint-bg);
        backdrop-filter: blur(22px) saturate(1.45) brightness(0.98);
        -webkit-backdrop-filter: blur(22px) saturate(1.45) brightness(0.98);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.22),
          inset -1px 0 0 rgba(255, 255, 255, 0.1),
          inset 0 -1px 0 rgba(255, 255, 255, 0.14),
          inset 0 0 0 1.5px var(--gb-tint-border),
          inset 1px 1px 0 rgba(0, 0, 0, 0.2),
          0 1px 3px rgba(0, 0, 0, 0.2),
          var(--gb-tint-shadow);
      }

      .glass-tile.gt-active:not(:disabled):hover {
        transform: translateY(-1.5px);
        background:
          radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255, 255, 255, 0.62) 0%, transparent 58%),
          radial-gradient(circle at 46% 44%, rgba(255, 255, 255, 0.86) 0%, rgba(255, 255, 255, 0.38) 38%, rgba(255, 255, 255, 0.08) 58%, transparent 72%),
          radial-gradient(circle at 70% 68%, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.18) 20%, transparent 34%),
          var(--gb-tint-bg-hover);
        box-shadow:
          inset -1px -1px 0 rgba(255, 255, 255, 0.78),
          inset -1px 0 0 rgba(255, 255, 255, 0.28),
          inset 0 -1px 0 rgba(255, 255, 255, 0.42),
          inset 0 0 0 1.5px var(--gb-tint-border),
          inset 1px 1px 0 rgba(15, 23, 42, 0.04),
          0 2px 6px rgba(15, 23, 42, 0.06),
          var(--gb-tint-shadow);
      }

      /* ── Icon tint (como badges) ───────────────────────────────────────── */
      .glass-tile.gt-idle.gt-sphere > .gt-sphere-icon-wrap svg,
      .glass-tile.gt-idle.gt-sphere > .gt-sphere-icon-wrap img {
        opacity: 0.88;
        transition: transform 320ms cubic-bezier(0.16, 1, 0.3, 1), opacity 280ms ease,
          filter 280ms ease;
      }

      .glass-tile.gt-idle.gt-sphere:not(:disabled):hover .gt-sphere-icon-wrap svg,
      .glass-tile.gt-active.gt-sphere:not(:disabled):hover .gt-sphere-icon-wrap svg {
        transform: scale(1.05);
        opacity: 0.95;
      }

      @media (prefers-reduced-motion: reduce) {
        .gt-particle {
          display: none !important;
        }
      }

      .glass-tile.gt-idle svg,
      .glass-tile.gt-idle img {
        filter: var(--cab-icon-filter-idle, none);
        opacity: 0.85;
        transition: filter 280ms ease, opacity 280ms ease;
      }
      .dark .glass-tile.gt-idle svg,
      .dark .glass-tile.gt-idle img {
        filter: var(--cab-icon-filter-idle-dark, none);
        opacity: 0.72;
      }
      .glass-tile.gt-idle:not(:disabled):hover svg,
      .glass-tile.gt-idle:not(:disabled):hover img {
        opacity: 0.92;
      }
      .glass-tile.gt-active svg,
      .glass-tile.gt-active img {
        filter: var(--cab-icon-filter-idle, none);
        opacity: 0.92;
      }
      .dark .glass-tile.gt-active svg,
      .dark .glass-tile.gt-active img {
        filter: var(--cab-icon-filter-idle-dark, none);
        opacity: 0.88;
      }
    `}</style>
  );
}
